import { useEffect, useRef } from 'react';
import './PageHeader.css';

/**
 * Shared page header — ONE consistent header for every page so they can't drift.
 * All dimensions are locked in PageHeader.css.
 *
 * Props:
 *   icon     — a lucide-react icon component (shown in the gold icon box)
 *   logoUrl  — optional image URL; when set, shown in the icon box instead of the icon
 *   title    — page title (Playfair serif, green, 28px)
 *   subtitle — optional grey subtitle line
 *   children — optional right-aligned actions
 *
 * Keeps itself in view (Stuart, 16 Sept): when something on the page
 * collapses (a fixture card closes, a sponsor code folds, the club card
 * unfolds) the page gets shorter but the browser keeps its scroll position,
 * so the title was left half-hidden under the sticky navbar. A
 * ResizeObserver on the document watches for the page SHRINKING; if the
 * header is then partly out of view, it scrolls back to the top. Growth is
 * left alone — scrolling someone back up while they open things would be
 * worse than the cut-off title.
 */
export default function PageHeader({ icon: Icon, logoUrl, title, subtitle, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    let lastHeight = document.documentElement.scrollHeight;
    const ro = new ResizeObserver(() => {
      const height = document.documentElement.scrollHeight;
      const shrank = height < lastHeight;
      lastHeight = height;
      if (!shrank || !ref.current) return;
      const nav = document.querySelector('.navbar');
      const navBottom = nav ? nav.getBoundingClientRect().bottom : 0;
      const rect = ref.current.getBoundingClientRect();
      // Only the "cut off" case: the header straddles the navbar's bottom
      // edge. Fully scrolled past it (collapsing something far down a long
      // page) is left alone — yanking to the top there would be worse.
      if (rect.top < navBottom && rect.bottom > navBottom) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    ro.observe(document.body);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="page-header" ref={ref}>
      <div className="page-header-inner">
        {logoUrl ? (
          <div className="page-header-icon" style={{ padding: 0, overflow: 'hidden' }}>
            <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : Icon && (
          <div className="page-header-icon">
            <Icon size={24} color="#fff" />
          </div>
        )}
        <div className="page-header-text">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {children && <div className="page-header-actions">{children}</div>}
      </div>
    </div>
  );
}
